/**
 * Gyro Admin's half of the usage record (the usage analytics contract, section 4): the console shares the widget's capture
 * module (@exante/gyro-react usage/featureEvents.ts) with surface `admin`, the admin cookie session as its auth, and one
 * screen per page: `admin:<PageId>` for every page in shell/routes.ts, `admin:fleet:<view>` for Fleet health's views
 * and `admin:pulse:<tab>` for the Pulse rail group's pages (Trends has no tabs: `admin:trends`). The server hides every
 * `admin:*` name from the rankings by default (hide_admin=1).
 */
import type { UsageEnvelope } from '@exante/gyro-react';
import { requestInit } from './api';
import type { PageId } from '../shell/routes';

/** `POST /v1/usage/events` under the console's session cookie; the HTTP status is the capture's to judge. */
export async function adminUsageSink(envelope: UsageEnvelope, options: { keepalive: boolean }): Promise<number> {
  const res = await fetch('/v1/usage/events', requestInit({ method: 'POST', body: JSON.stringify(envelope), keepalive: options.keepalive, signal: AbortSignal.timeout(30000) }));
  return res.status;
}

/** A query value as a screen name part: the characters a name may carry, at most 40 of them. */
function namePart(value: string | null): string | null {
  if (!value) return null;
  const part = value.replace(/[^A-Za-z0-9_.-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
  return part || null;
}

/** The console's screen name for a page and its query (`?view=` on Fleet health). */
export function adminScreenFor(page: PageId, search = ''): string {
  const query = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (page === 'fleet') return `admin:fleet:${namePart(query.get('view')) ?? 'overview'}`;
  if (page === 'pulse') return 'admin:pulse:overview';
  if (page.startsWith('pulse-')) return `admin:pulse:${page.slice('pulse-'.length)}`;
  return `admin:${page}`;
}
